
import { 
  CharacterFeature,
  // CharacterItem,
  AbilityEffect,
  CharacterSheetInterface,
  ExtraEffectsFeature,
  ExtraEffectsOfTypeFeature,
  ExtraEffectTypeFeature,
  TrueCreatureInstance,
  CharacterAction,
  Character,
  WeaponLimitation,
  AbilityLimitation,
  ItemLimitation,
  SpellsFromList,
  SpellModifier,
  SpellsFromListWithCosts,
  // CharacterFeatureSource,
} from "@dndModels";
import { AttackType, SpellLevel, TimeRestriction } from "@dndModels/Types";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const char_util = DandDCharacterUtilities.getInstance();

export class CharacterExtraEffects {
  true_id = "";
  char_features: CharacterFeature[] = [];
  // special_resource_used: number = 0;
  special_resource_amount = -1;
  // special_resource_refresh_rule: string = "";
  _slot_level: SpellLevel = -1;
  _effects: AbilityEffect[] = [];

  constructor(obj?: any) {
    if (obj) {
      this.true_id = obj.true_id ? obj.true_id : "";
      // this.special_resource_used = obj.special_resource_used ? obj.special_resource_used : 0;
    }
  }

  get effect_cfs(): CharacterFeature[] {
    return this.char_features.filter(o => 
      o.floating_feature instanceof ExtraEffectsFeature || 
      o.floating_feature instanceof ExtraEffectsOfTypeFeature);
  }

  get effects(): AbilityEffect[] {
    if (this._effects.length === 0) {
      return this.effect_cfs.flatMap(o => {
        if (o.floating_feature instanceof ExtraEffectsFeature || 
          o.floating_feature instanceof ExtraEffectsOfTypeFeature) {
          return o.floating_feature.effects;
        }
        return [];
      });
    } else {
      return this._effects;
    }
  }

  set effects(value: AbilityEffect[]) {
    this._effects = value;
  }

  get main_cf(): CharacterFeature {
    if (this.char_features.length === 1) {
      const cf = this.char_features[0];
      const ff = cf.floating_feature;
      if (ff instanceof ExtraEffectsFeature ||
        ff instanceof SpellsFromList ||
        ff instanceof SpellsFromListWithCosts
      ) {
        return cf;
      } else if (ff instanceof ExtraEffectTypeFeature) {
        return cf;
      }
    } else {
      const type_cf = this.char_features.find(o => o.floating_feature instanceof ExtraEffectTypeFeature);
      if (type_cf && type_cf.floating_feature instanceof ExtraEffectTypeFeature) {
        return type_cf;
      } 
    }
    return new CharacterFeature();
  }

  get main_ff() {
    const ff = this.main_cf.floating_feature;
    return ff instanceof ExtraEffectsFeature || 
      ff instanceof ExtraEffectTypeFeature || 
      ff instanceof SpellsFromList ||
      ff instanceof SpellsFromListWithCosts ||
      ff instanceof SpellModifier ? 
      ff : new ExtraEffectsFeature();
  }

  get apply_to() {
    const ff = this.main_ff;
    if (ff instanceof SpellsFromList ||
      ff instanceof SpellsFromListWithCosts || 
      ff instanceof SpellModifier) {
      return ["From List"];
    } else {
      return ff.apply_to;
    }
  }
  get weapon_limit() {
    const ff = this.main_ff;
    if (ff instanceof SpellsFromList ||
      ff instanceof SpellsFromListWithCosts || 
      ff instanceof SpellModifier) {
      return new WeaponLimitation();
    } else {
      return ff.weapon_limit;
    }
  }
  get ability_limit() {
    const ff = this.main_ff;
    if (ff instanceof SpellsFromList ||
      ff instanceof SpellsFromListWithCosts || 
      ff instanceof SpellModifier) {
      return new AbilityLimitation();
    } else {
      return ff.ability_limit;
    }
  }
  get spell_limit() {
    return this.main_ff.spell_limit;
  }
  get item_limit() {
    return new ItemLimitation();
  }

  get name(): string {
    const ff = this.main_ff;
    if (ff) {
      if (ff instanceof SpellsFromList ||
        ff instanceof SpellsFromListWithCosts || 
        ff instanceof SpellModifier) {
        return ff.use_name;
      } else {
        return ff.ee_type;
      }
    }
    return "";
  }

  get description(): string {
    const ff = this.main_ff;
    if (ff) {
      return ff.description;
    }
    return "";
  }

  get true_name(): string {
    return this.name;
  }

  get attack_types(): AttackType[] {
    return this.effects.map(effect => { return effect.attack_type; });
  }

  // get damage_types(): string[] {
  //   return this.effects.flatMap(effect => { return effect.damage_types; });
  // }
  
  get time_restriction(): TimeRestriction {
    const ff = this.main_ff;
    if (ff instanceof SpellsFromList ||
      ff instanceof SpellsFromListWithCosts || 
      ff instanceof SpellModifier) {
      return "Every Time";
    } else {
      return ff.time_restriction;
    }
  }
  get extra_restriction() {
    const ff = this.main_ff;
    if (ff instanceof SpellsFromList ||
      ff instanceof SpellsFromListWithCosts || 
      ff instanceof SpellModifier) {
      return "";
    } else {
      return ff.extra_restriction;
    }
  }

  get special_resource_refresh_rule() {
    return this.main_ff.consumed_resource.special_resource_refresh_rule;
  }
  get source_id() {
    return this.main_cf.source_id;
  }
  get source_name() {
    return this.main_cf.source_name;
  }
  get source_true_id() {
    return this.main_cf.source_true_id;
  }
  get source_type() {
    return this.main_cf.source_type;
  }
  get item_id() {
    return this.main_cf.item_id;
  }
  get item_name() {
    return this.main_cf.item_name;
  }
  get use_item_id() {
    return this.main_cf.use_item_id;
  }
  get use_item_name() {
    return this.main_cf.use_item_name;
  }

  get amount_consumed() {
    return this.main_ff.consumed_resource.amount_consumed;
  }
  get resource_consumed() {
    return this.main_ff.consumed_resource.resource_consumed;
  }
  get slot_level() {
    if (this._slot_level === -1) {
      return this.main_ff.consumed_resource.slot_level;
    } else {
      return this._slot_level;
    }
  }
  set slot_level(value: SpellLevel) {
    this._slot_level = value;
  }

  get potence_class() {
    const ff = this.main_ff;
    return ff.potence_class;
  }
  
  get ancestries() {
    return this.main_cf.ancestries;
  }

  disabled({ 
    char, 
    action, 
    level = -1, 
    type = "" 
  }: { 
    char: CharacterSheetInterface | TrueCreatureInstance, 
    action?: CharacterAction, 
    level?: number, 
    type?: string 
  }) {
    const ff = this.main_ff;
    if (ff && char instanceof Character) {
      const resource_consumed = ff.consumed_resource.resource_consumed;
      const amount_consumed = ff.consumed_resource.amount_consumed;
      if (resource_consumed === "Special" && ["","Special"].includes(type)) {
        const special_resource_used = char.body.resource_holder.getSpecialResourceUsed(this.true_id);
        return special_resource_used + +amount_consumed > +this.special_resource_amount;
      } else if (resource_consumed === "Slot" || resource_consumed === "Pact" || !["","Special"].includes(type)) {
        if (level === -1) {
          level = ff.consumed_resource.slot_level;
        }
        const filtered_slots = resource_consumed === "Pact" ?
          char.body.use_slots.filter(o => 
            o.level === level &&
            o.slot_name.includes("Pact")
          )
        : char.body.use_slots.filter(o => 
          o.level === level &&
          (ff.consumed_resource.slot_type === "" || o.type_id === ff.consumed_resource.slot_type) &&
          (type === "" || o.type_id === type)
        );
        // if (this.name.includes("Eldritch Smite")) {
        //   console.log(resource_consumed);
        //   console.log(level);
        //   console.log(filtered_slots);
        // }
        if (filtered_slots.length === 1) {
          const slot = filtered_slots[0];
          return slot.used + +amount_consumed > slot.total;
        } else {
          return false;
        }
      } else {
        const resource_finder = char.resources.filter(o => 
          o.type_id === resource_consumed);
        if (resource_finder.length === 1) {
          const resource = resource_finder[0];
          return resource.used + +amount_consumed > resource.total;
        }
      }
    }
    return false;
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      // special_resource_used: this.special_resource_used,
    };
  }
  
  calc_special_resource_amount(char: CharacterSheetInterface) {
    const ff = this.main_ff;
    if (ff && ff.consumed_resource.resource_consumed === "Special") {
      this.special_resource_amount = char_util.value(ff.consumed_resource.special_resource_amount, { obj: char, source_id: this.source_true_id });
      // this.special_resource_refresh_rule = mainFeature.special_resource_refresh_rule;
    }
  }

  clone(drop_ids = false): CharacterExtraEffects {
    const new_obj = new CharacterExtraEffects();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  cloneEmpty() {
    const new_obj = new CharacterExtraEffects();
    new_obj.true_id = this.true_id;
    // new_obj.special_resource_used = this.special_resource_used;
    return new_obj;
  }

  copy(copyMe: CharacterExtraEffects, drop_ids = false) {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.char_features = copyMe.char_features.map(o => o.clone());
  }
}